import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';
import { Button } from '@ui/components/ui/button';
import { UserEntity } from '@data/index';

// The account: the address you sign in with, and the password you sign in with.
//
// Opens on both of those rather than on a form, the way the two-factor section opens on its status.
// Changing the password swaps in its own form (see ChangePasswordSection), so this stays a summary.
//
// The email is shown but not editable: changing it is not something this app can do, so a disabled field
// says so more plainly than leaving it out and hoping nobody goes looking for it.
const AccountSection = ({
  user,
  updated,
  onChangePassword,
}: {
  user: UserEntity;
  // Set after a change, so the confirmation lands here rather than on the page that has been left.
  updated: boolean;
  onChangePassword: () => void;
}): JSX.Element => {
  const { t } = useTranslation();

  return (
    <section className="space-y-5">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="settings-email">{t('settings.account.email')}</Label>
        <Input id="settings-email" type="email" value={user.email ?? ''} disabled readOnly />
        <p className="text-xs text-muted-foreground">{t('settings.account.emailHint')}</p>
      </div>

      {/* No field here on purpose: a masked input would suggest the password is ours to show, and there is
          nothing to type until Change password is pressed. */}
      <div className="flex flex-col gap-1.5">
        <p className="text-sm font-medium leading-none text-foreground">{t('settings.password.title')}</p>
        {updated ? (
          <p className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="size-3.5" />
            {t('settings.password.updated')}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">{t('settings.password.summary')}</p>
        )}
        <div className="mt-1.5">
          <Button variant="outline" onClick={onChangePassword}>
            {t('settings.password.change')}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default AccountSection;
